import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { SearchableSelect } from '../components/ui/searchable-select';
import { FormRow, FormSection, ReadOnlyField } from '../components/FormRow';
import { DateTimeInput, DateInput } from '../components/DateTimeInput';
import { loadAPI, customerAPI, carrierAPI, facilityAPI, accessorialAPI, interactionAPI, documentAPI, userAPI, fuelPriceAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { toastApiError } from '../lib/utils';
import { ArrowLeft, Plus, Trash2, Mail, Phone, Globe, MapPin, ArrowUp, ArrowDown, Copy, Share2, Building2, FileText, Upload, Users, Camera, X, Image, Download, Printer, History, RefreshCw, UserX, Package, Truck, DollarSign, User, Calendar, Weight, Layers, Pencil } from 'lucide-react';
import { StatusBadge, AppointmentBadge } from '../components/StatusBadge';
import BOLGenerator from '../components/BOLGenerator';
import { FinancialsTab } from '../components/load-detail/FinancialsTab';
import { NotesTab } from '../components/load-detail/NotesTab';
import { CrossBorderTab } from '../components/load-detail/CrossBorderTab';

const LoadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isSuperAdmin, isAdmin, isCarrier, isCustomer, canAccessCommission } = useAuth();
  const isNew = !id || id === 'new';
  const canEditAMOP = isSuperAdmin() || isAdmin();
  const isCarrierUser = isCarrier();
  const isCustomerUser = isCustomer();
  const showCommission = canAccessCommission();
  
  // Carrier users can only see these statuses
  const carrierStatuses = ['At the Shipper', 'In Transit', 'In Transit MX', 'In Transit US', 'At Delivery', 'Delivered'];
  
  // Fields that carrier users can edit
  const carrierEditableFields = [
    'dispatch_name', 'dispatch_phone', 'driver_name', 'driver_phone',
    'truck_number', 'truck_lp', 'truck_vin', 'trailer_number', 'trailer_lp'
  ];
  
  const [load, setLoad] = useState({
    customer_id: '', customer_name: '', commodity: '', equipment: '',
    cross_border: false, stops: [], line_haul: 0, customer_rate: 0,
    carrier_id: '', carrier_name: '', driver_name: '', driver_phone: '',
    truck_number: '', trailer_number: '', carrier_pay: 0, status: 'Available',
    shipping_date: '', weight: '', pallet_count: '', load_number: '',
    dispatch_name: '', dispatch_phone: '',
    truck_lp: '', truck_vin: '', trailer_lp: '',
    covered_at: null, rate_con_sent_at: null,
    carrier_line_haul: 0, customer_line_haul: 0,
    carrier_accessorials: [], customer_accessorials: [],
    temperature: '', reefer_mode: '',
    border_arrival_date_mx: null,
    border_arrival_date_us: null,
    crossing_docs: { ccp_layout: false, cfdi: false, w9: false, doda: false, us_entry: false, redo_documents: false },
    crossing_docs_received_date: null,
    crossing_docs_due_date: null,
    crossing_eta_date: null,
    // Drop Off Trailer fields
    drop_off_trailer: false,
    trailer_dropped_date: null,
    trailer_ready_for_pickup_date: null,
    trailer_pulled_date: null,
    // Transfer fields (Cross-Border)
    transfer_name: '',
    transfer_scac: '',
    transfer_caat: '',
    // Account Manager & Operators (from Customer)
    account_manager_id: '',
    account_manager_name: '',
    account_manager_commission_pct: 0,
    // Operator 1
    operator_id: '',
    operator_name: '',
    operator_commission_pct: 0,
    // Operator 2
    operator2_id: '',
    operator2_name: '',
    operator2_commission_pct: 0
  });
  const [customers, setCustomers] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [accessorials, setAccessorials] = useState([]);
  const [interactions, setInteractions] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]); // Account Managers and Operators
  const [loading, setLoading] = useState(!isNew);
  const [activeTab, setActiveTab] = useState('summary');
  const [localAccessorials, setLocalAccessorials] = useState([]);
  const [hasChanges, setHasChanges] = useState(isNew);
  const [originalLoad, setOriginalLoad] = useState(null);
  const [changedFields, setChangedFields] = useState({});
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [cbDocuments, setCbDocuments] = useState([]);
  const [showAddDocModal, setShowAddDocModal] = useState(false);
  const [newDocName, setNewDocName] = useState('');
  const [newDocUrl, setNewDocUrl] = useState('');
  const [newDocNotes, setNewDocNotes] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadMode, setUploadMode] = useState('file'); // 'file' or 'url'
  const [uploading, setUploading] = useState(false);
  // Appointment screenshot state
  const [apptScreenshotModal, setApptScreenshotModal] = useState({ open: false, stopIndex: null });
  const [apptImagePreview, setApptImagePreview] = useState({ open: false, imageUrl: '', stopIndex: null });
  const [uploadingApptScreenshot, setUploadingApptScreenshot] = useState(false);
  const [isDraggingAppt, setIsDraggingAppt] = useState(false);
  const [apptDateTimeErrors, setApptDateTimeErrors] = useState({}); // Track stops with missing datetime
  
  // FSC auto-calculation metadata (price used + week of)
  const [fscAutoInfo, setFscAutoInfo] = useState(null);
  const [fscAutoLoading, setFscAutoLoading] = useState(false);
  const [fscManualOverride, setFscManualOverride] = useState(false);
  
  // Handle drag events for appointment screenshot
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingAppt(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingAppt(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingAppt(false);
    
    const files = e.dataTransfer?.files;
    if (files && files.length > 0 && apptScreenshotModal.stopIndex !== null) {
      const file = files[0];
      // Check if it's an image
      if (file.type.startsWith('image/')) {
        handleApptScreenshotUpload(apptScreenshotModal.stopIndex, file);
      } else {
        toast.error('Please drop an image file (PNG, JPG, JPEG, GIF)');
      }
    }
  };


  // Derive the ship date (first pickup stop appointment, or fallback to shipping_date)
  const shipDateISO = useMemo(() => {
    const firstPickup = (load.stops || []).find(s => s.type === 'pickup' && s.appointment_datetime);
    const raw = firstPickup?.appointment_datetime || load.shipping_date;
    if (!raw) return null;
    // Normalize to YYYY-MM-DD
    try {
      return new Date(raw).toISOString().slice(0, 10);
    } catch {
      return String(raw).slice(0, 10);
    }
  }, [load.stops, load.shipping_date]);

  // Auto-populate FSC when customer + ship date are both set (and not manually overridden)
  useEffect(() => {
    // Guard against empty/whitespace customer_id (was causing 422s on the lookup endpoint)
    const validCustomerId = typeof load.customer_id === 'string' && load.customer_id.trim().length > 0;
    if (!validCustomerId || !shipDateISO || fscManualOverride) return;
    let cancelled = false;
    const run = async () => {
      setFscAutoLoading(true);
      try {
        const res = await fuelPriceAPI.lookupFSC(load.customer_id, null, shipDateISO);
        if (cancelled) return;
        const data = res.data || {};
        if (data.fsc_value === null || data.fsc_value === undefined) {
          setFscAutoInfo({ ...data, no_match: true });
          return;
        }
        setFscAutoInfo(data);
        // Apply to load — preserve user overrides only if fscManualOverride
        setLoad(prev => {
          const isPercentage = (data.table_type || 'flat') === 'percentage';
          const customerLineHaul = prev.customer_line_haul || 0;
          const customerMileage = prev.customer_mileage || 0;
          const updates = { fsc_enabled: true };
          if (isPercentage) {
            updates.fsc_percentage = data.fsc_value;
            updates.fsc_rpm = null;
            updates.fsc_amount = customerLineHaul ? Math.round(customerLineHaul * data.fsc_value / 100 * 100) / 100 : 0;
          } else {
            updates.fsc_rpm = data.fsc_value;
            updates.fsc_percentage = null;
            updates.fsc_amount = customerMileage ? Math.round(customerMileage * data.fsc_value * 100) / 100 : 0;
          }
          return { ...prev, ...updates };
        });
      } catch (e) {
        if (!cancelled) setFscAutoInfo(null);
      } finally {
        if (!cancelled) setFscAutoLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [load.customer_id, shipDateISO, load.customer_line_haul, load.customer_mileage, fscManualOverride]);

  // Track changes and which fields changed
  useEffect(() => {
    if (isNew) {
      setHasChanges(true);
    } else if (originalLoad) {
      const changed = JSON.stringify(load) !== JSON.stringify(originalLoad) ||
                     JSON.stringify(localAccessorials) !== JSON.stringify(originalLoad.local_accessorials || []);
      setHasChanges(changed);
      
      // Track which specific fields changed
      const fields = {};
      Object.keys(load).forEach(key => {
        if (key === 'stops') {
          // Track individual stop field changes
          const originalStops = originalLoad.stops || [];
          const currentStops = load.stops || [];
          
          // Check if number of stops changed
          if (currentStops.length !== originalStops.length) {
            fields.stops = true;
            fields.stops_changed = {}; // Mark all stops as changed
            currentStops.forEach((_, idx) => {
              fields.stops_changed[idx] = true;
            });
          } else {
            // Check each stop individually
            const stopsChanged = {};
            currentStops.forEach((stop, idx) => {
              const origStop = originalStops[idx];
              if (origStop && JSON.stringify(stop) !== JSON.stringify(origStop)) {
                stopsChanged[idx] = true;
              }
            });
            if (Object.keys(stopsChanged).length > 0) {
              fields.stops = true;
              fields.stops_changed = stopsChanged;
            }
          }
        } else if (JSON.stringify(load[key]) !== JSON.stringify(originalLoad[key])) {
          fields[key] = true;
        }
      });
      // Check if accessorials changed
      if (JSON.stringify(localAccessorials) !== JSON.stringify(originalLoad.local_accessorials || [])) {
        fields.local_accessorials = true;
      }
      setChangedFields(fields);
    }
  }, [load, localAccessorials, originalLoad, isNew]);

  // Helper to get field class based on whether it changed
  // Uses Tailwind classes for proper dark mode contrast (amber bg + dark text)
  const getFieldClass = (fieldName) => {
    if (changedFields[fieldName]) {
      return 'bg-amber-100 dark:bg-amber-200 text-gray-900 dark:text-gray-900';
    }
    return '';
  };

  // Helper to get class for individual stop fields - only highlights if that specific stop changed
  const getStopFieldClass = (stopIndex) => {
    if (changedFields.stops_changed && changedFields.stops_changed[stopIndex]) {
      return 'bg-amber-100 dark:bg-amber-200 text-gray-900 dark:text-gray-900';
    }
    return '';
  };

  // Helper to check if a specific tab has unsaved changes
  const tabHasChanges = useMemo(() => {
    // Define which fields belong to which tab
    const summaryFields = [
      'customer_id', 'customer_name', 'load_number', 'shipping_date', 'status',
      'commodity', 'weight', 'pallet_count', 'equipment', 'cross_border',
      'temperature', 'reefer_mode', 'drop_off_trailer', 'trailer_dropped_date',
      'trailer_ready_for_pickup_date', 'trailer_pulled_date'
    ];
    const stopsFields = ['stops'];
    const carrierFields = [
      'carrier_id', 'carrier_name', 'dispatch_name', 'dispatch_phone',
      'driver_name', 'driver_phone', 'truck_number', 'truck_lp', 'truck_vin',
      'trailer_number', 'trailer_lp', 'covered_at', 'rate_con_sent_at'
    ];
    const crossBorderFields = [
      'transfer_name', 'transfer_scac', 'transfer_caat',
      'crossing_docs', 'crossing_docs_received_date', 'crossing_docs_due_date',
      'crossing_eta_date'
    ];
    const financialsFields = [
      'carrier_line_haul', 'customer_line_haul', 'carrier_accessorials',
      'customer_accessorials', 'local_accessorials', 'account_manager_id',
      'account_manager_commission_pct', 'operator_id', 'operator_commission_pct',
      'operator2_id', 'operator2_commission_pct'
    ];

    const checkFields = (fields) => fields.some(field => changedFields[field]);

    return {
      summary: checkFields(summaryFields),
      stops: changedFields.stops || false,
      carrier: checkFields(carrierFields),
      'cross-border': checkFields(crossBorderFields),
      financials: checkFields(financialsFields),
      notes: false, // Notes are saved separately
      documents: false // Documents are saved separately
    };
  }, [changedFields]);

  const fetchDropdownData = useCallback(async () => {
    try {
      const [customersRes, carriersRes, facilitiesRes, usersRes] = await Promise.all([
        customerAPI.getAll(),
        carrierAPI.getAll(),
        facilityAPI.getAll(),
        userAPI.getDropdown()
      ]);
      setCustomers(customersRes.data);
      setCarriers(carriersRes.data);
      setFacilities(facilitiesRes.data);
      // Filter to only account managers and operators
      setStaffUsers(usersRes.data.filter(u => ['account_manager', 'operator', 'super_admin', 'admin'].includes(u.role)));
    } catch (error) {
      toast.error('Failed to load data');
    }
  }, []);

  const fetchLoadData = useCallback(async () => {
    try {
      const [loadRes, accessRes, interRes, docsRes] = await Promise.all([
        loadAPI.getById(id),
        accessorialAPI.getByLoad(id),
        interactionAPI.getByEntity('load', id),
        documentAPI.getByEntity('load', id)
      ]);
      const loadData = loadRes.data;
      // Initialize missing fields with defaults
      const normalizedLoad = {
        ...loadData,
        dispatch_name: loadData.dispatch_name || '',
        dispatch_phone: loadData.dispatch_phone || '',
        truck_lp: loadData.truck_lp || '',
        truck_vin: loadData.truck_vin || '',
        trailer_lp: loadData.trailer_lp || '',
        covered_at: loadData.covered_at || null,
        rate_con_sent_at: loadData.rate_con_sent_at || null,
        carrier_line_haul: loadData.carrier_line_haul || loadData.carrier_pay || 0,
        customer_line_haul: loadData.customer_line_haul || loadData.customer_rate || 0,
        crossing_docs: loadData.crossing_docs || { ccp_layout: false, cfdi: false, w9: false, doda: false, us_entry: false, redo_documents: false }
      };
      setLoad(normalizedLoad);
      setOriginalLoad({ ...normalizedLoad, local_accessorials: loadData.local_accessorials || [] });
      setAccessorials(accessRes.data);
      setLocalAccessorials(loadData.local_accessorials || []);
      setInteractions(interRes.data);
      // Filter for cross-border documents
      setCbDocuments(docsRes.data.filter(doc => doc.document_type === 'cross_border'));
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDropdownData();
    if (!isNew) fetchLoadData();
  }, [id, isNew, fetchDropdownData, fetchLoadData]);

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    if (load.stops.length < 2) {
      toast.error('Load must have at least one pickup and one delivery');
      return;
    }
    // Validate first stop is pickup and last is delivery
    if (load.stops[0]?.type !== 'pickup') {
      toast.error('First stop must be a Pickup');
      return;
    }
    if (load.stops[load.stops.length - 1]?.type !== 'delivery') {
      toast.error('Last stop must be a Delivery');
      return;
    }
    
    // Validate appointment datetime is required for Requested, Granted, Updated TMS statuses
    const statusesRequiringDateTime = ['Requested', 'Granted', 'Updated TMS'];
    const dateTimeErrors = {};
    let hasDateTimeErrors = false;
    
    load.stops.forEach((stop, index) => {
      if (statusesRequiringDateTime.includes(stop.appointment_status) && !stop.appointment_datetime) {
        dateTimeErrors[index] = true;
        hasDateTimeErrors = true;
      }
    });
    
    if (hasDateTimeErrors) {
      setApptDateTimeErrors(dateTimeErrors);
      setActiveTab('stops'); // Switch to stops tab to show errors
      toast.error('Appointment Date/Time is required for Requested, Granted, or Updated TMS status');
      return;
    }
    
    // Clear any previous errors
    setApptDateTimeErrors({});
    
    try {
      // Enrich stops with facility data before saving
      const enrichedStops = (load.stops || []).map(stop => {
        if (stop.facility_id) {
          const facility = facilities.find(f => f.id === stop.facility_id);
          if (facility) {
            return {
              ...stop,
              facility_name: stop.facility_name || facility.name || '',
              address: stop.address || facility.address || '',
              city: stop.city || facility.city || '',
              state: stop.state || facility.state || '',
              zip_code: stop.zip_code || facility.zip_code || ''
            };
          }
        }
        return stop;
      });
      const saveData = {
        ...load,
        stops: enrichedStops,
        local_accessorials: localAccessorials
      };
      if (isNew) {
        const response = await loadAPI.create(saveData);
        toast.success('Load created successfully');
        navigate(`/loads/${response.data.id}`);
      } else {
        // Save the load
        const response = await loadAPI.update(id, saveData);
        const updatedLoad = response.data;
        
        // Normalize the response to ensure all fields exist
        const normalizedLoad = {
          ...updatedLoad,
          stops: updatedLoad.stops || [],
          dispatch_name: updatedLoad.dispatch_name || '',
          dispatch_phone: updatedLoad.dispatch_phone || '',
          truck_lp: updatedLoad.truck_lp || '',
          truck_vin: updatedLoad.truck_vin || '',
          trailer_lp: updatedLoad.trailer_lp || '',
          covered_at: updatedLoad.covered_at || null,
          rate_con_sent_at: updatedLoad.rate_con_sent_at || null,
          carrier_line_haul: updatedLoad.carrier_line_haul || updatedLoad.carrier_pay || 0,
          customer_line_haul: updatedLoad.customer_line_haul || updatedLoad.customer_rate || 0,
          crossing_docs: updatedLoad.crossing_docs || { ccp_layout: false, cfdi: false, w9: false, doda: false, us_entry: false, redo_documents: false }
        };
        
        toast.success('Load saved successfully');
        setLoad(normalizedLoad);
        setOriginalLoad({ ...normalizedLoad, local_accessorials: localAccessorials });
        setChangedFields({});
        // Stay on the same page - no navigation
      }
    } catch (error) {
      toastApiError(toast, error, 'Load could not be saved');
    }
  };

  // Get facility city, state for a stop
  const getStopCityState = (stop) => {
    if (!stop) return 'N/A';
    
    // First try to find facility in the facilities array
    if (stop.facility_id) {
      const facility = facilities.find(f => f.id === stop.facility_id);
      if (facility && facility.city && facility.state) {
        return `${facility.city}, ${facility.state}`;
      }
    }
    
    // Fallback to facility_name if available
    if (stop.facility_name) {
      return stop.facility_name;
    }
    
    return 'N/A';
  };

  // Format datetime for display
  const formatDateTime = (datetime) => {
    if (!datetime) return '';
    try {
      return new Date(datetime).toLocaleString('en-US', { 
        month: 'numeric', 
        day: 'numeric', 
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true 
      });
    } catch {
      return datetime;
    }
  };

  // Share Load - Copy load info to clipboard
  const handleShareLoad = () => {
    const pickupStops = load.stops?.filter(s => s.type === 'pickup') || [];
    const deliveryStops = load.stops?.filter(s => s.type === 'delivery') || [];
    const origin = pickupStops[0];
    const lastDest = deliveryStops[deliveryStops.length - 1];
    
    const originCity = getStopCityState(origin);
    const destCity = getStopCityState(lastDest);
    
    let shareText = '';
    
    // Row 1: Origin City, State to Last Destination City, State
    shareText += `${originCity} to ${destCity}\n`;
    
    // Row 2: Weight, Commodity, Equipment, if reefer add Temperature Range and Cycle
    const parts = [];
    if (load.weight) parts.push(`${load.weight} lbs`);
    if (load.commodity) parts.push(load.commodity);
    if (load.equipment) parts.push(load.equipment);
    if (load.equipment === 'Reefer') {
      if (load.temperature) parts.push(`Temperature ${load.temperature}`);
      if (load.reefer_mode) parts.push(load.reefer_mode);
    }
    if (parts.length > 0) {
      shareText += `${parts.join(', ')}\n`;
    }
    
    // Row 3: Pickup Date and time (format: M/D @ HHmm)
    const pickupDateTime = origin?.appointment_datetime || load.shipping_date;
    if (pickupDateTime) {
      const pickupDate = new Date(pickupDateTime);
      const month = pickupDate.getMonth() + 1;
      const day = pickupDate.getDate();
      const hours = pickupDate.getHours().toString().padStart(2, '0');
      const mins = pickupDate.getMinutes().toString().padStart(2, '0');
      shareText += `Pickup ${month}/${day} @ ${hours}${mins}\n`;
    }
    
    // Row 4: Delivery info
    if (deliveryStops.length === 1) {
      // Single destination
      const stop = deliveryStops[0];
      if (stop.appointment_datetime) {
        const delDate = new Date(stop.appointment_datetime);
        const month = delDate.getMonth() + 1;
        const day = delDate.getDate();
        const hours = delDate.getHours().toString().padStart(2, '0');
        const mins = delDate.getMinutes().toString().padStart(2, '0');
        shareText += `Deliver ${month}/${day} @ ${hours}${mins}\n`;
      }
    } else if (deliveryStops.length > 1) {
      // Multiple stops
      shareText += `Delivers:\n`;
      deliveryStops.forEach((stop) => {
        const city = getStopCityState(stop);
        if (stop.appointment_datetime) {
          const delDate = new Date(stop.appointment_datetime);
          const month = delDate.getMonth() + 1;
          const day = delDate.getDate();
          const hours = delDate.getHours().toString().padStart(2, '0');
          const mins = delDate.getMinutes().toString().padStart(2, '0');
          shareText += `${city} ${month}/${day} @ ${hours}${mins}\n`;
        } else {
          shareText += `${city} TBD\n`;
        }
      });
    }
    
    // Use textarea fallback method (more reliable across browsers)
    const textArea = document.createElement('textarea');
    textArea.value = shareText.trim();
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.width = '2em';
    textArea.style.height = '2em';
    textArea.style.padding = '0';
    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';
    textArea.style.background = 'transparent';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        toast.success('Load info copied to clipboard');
      } else {
        // If execCommand fails, try clipboard API as backup
        navigator.clipboard.writeText(shareText.trim())
          .then(() => toast.success('Load info copied to clipboard'))
          .catch(() => toast.error('Failed to copy load info'));
      }
    } catch (err) {
      toast.error('Failed to copy load info');
    }
    
    document.body.removeChild(textArea);
  };

  // Copy Load - Create a copy without dates and PO#s
  const handleCopyLoad = async () => {
    try {
      const copiedStops = load.stops.map(stop => ({
        ...stop,
        stop_number: stop.stop_number,
        type: stop.type,
        facility_id: stop.facility_id,
        facility_name: stop.facility_name,
        po_number: '', // Clear PO#
        appointment_status: 'Needs Scheduling',
        appointment_datetime: '', // Clear date
        tms_updated: false
      }));
      
      const copyData = {
        customer_id: load.customer_id,
        customer_name: load.customer_name,
        commodity: load.commodity,
        equipment: load.equipment,
        cross_border: load.cross_border,
        stops: copiedStops,
        line_haul: load.line_haul,
        customer_rate: load.customer_rate,
        shipping_date: '', // Clear date
        weight: load.weight,
        pallet_count: load.pallet_count,
        temperature: load.temperature,
        reefer_mode: load.reefer_mode
      };
      
      const response = await loadAPI.create(copyData);
      toast.success('Load copied successfully');
      navigate(`/loads/${response.data.id}`);
    } catch (error) {
      toast.error('Failed to copy load');
    }
  };

  const addStop = (type) => {
    const newStop = {
      stop_number: load.stops.length + 1,
      type,
      facility_id: '',
      facility_name: '',
      po_number: '',
      appointment_status: 'Needs Scheduling',
      appointment_datetime: '',
      tms_updated: false
    };
    
    let newStops = [...load.stops, newStop];
    // Re-number stops
    newStops = newStops.map((s, i) => ({ ...s, stop_number: i + 1 }));
    setLoad({ ...load, stops: newStops });
  };

  const updateStop = (index, field, value) => {
    setLoad(prev => {
      const updatedStops = prev.stops.map((stop, i) => {
        if (i === index) {
          const newStop = { ...stop, [field]: value };
          if (field === 'facility_id') {
            const facility = facilities.find(f => f.id === value);
            if (facility) {
              newStop.facility_name = facility.name;
              newStop.city = facility.city || '';
              newStop.state = facility.state || '';
              newStop.zip_code = facility.zip_code || '';
              newStop.address = facility.address || '';
            }
          }
          return newStop;
        }
        return stop;
      });
      return { ...prev, stops: updatedStops };
    });
  };

  const removeStop = (index) => {
    let newStops = load.stops.filter((_, i) => i !== index);
    // Re-number stops
    newStops = newStops.map((s, i) => ({ ...s, stop_number: i + 1 }));
    setLoad({ ...load, stops: newStops });
  };

  const moveStop = (index, direction) => {
    const newStops = [...load.stops];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newStops.length) return;
    
    [newStops[index], newStops[newIndex]] = [newStops[newIndex], newStops[index]];
    // Re-number stops
    const renumbered = newStops.map((s, i) => ({ ...s, stop_number: i + 1 }));
    setLoad({ ...load, stops: renumbered });
  };

  const handleCarrierChange = (carrierId) => {
    const carrier = carriers.find(c => c.id === carrierId);
    const newStatus = carrierId ? 'Covered' : load.status;
    const coveredAt = carrierId && load.status !== 'Covered' ? new Date().toISOString() : load.covered_at;
    
    setLoad({ 
      ...load, 
      carrier_id: carrierId, 
      carrier_name: carrier?.name || '',
      status: newStatus,
      covered_at: coveredAt
    });
    
    if (carrierId && load.status !== 'Covered') {
      toast.success('Status changed to Covered');
    }
  };

  const handleStatusCheckbox = (statusField, checked) => {
    const timestamp = checked ? new Date().toISOString() : null;
    let newStatus = load.status;
    
    if (statusField === 'covered' && checked) {
      newStatus = 'Covered';
    } else if (statusField === 'rate_con_sent' && checked) {
      newStatus = 'Rate Con Sent';
    }
    
    setLoad({
      ...load,
      [statusField === 'covered' ? 'covered_at' : 'rate_con_sent_at']: timestamp,
      status: newStatus
    });
  };

  // Uncover - Clear carrier info and set status to Available
  const handleUncover = async () => {
    
    // REMOVED confirmation dialog - just do it directly
    const uncoveredLoad = {
      ...load,
      status: 'Available',
      carrier_id: null,
      carrier_name: '',
      dispatch_name: '',
      dispatch_phone: '',
      driver_name: '',
      driver_phone: '',
      truck_number: '',
      truck_lp: '',
      truck_vin: '',
      trailer_number: '',
      trailer_lp: '',
      covered_at: null,
      rate_con_sent_at: null
    };
    
    // Auto-save the uncovered load
    try {
      toast.info('Uncovering load...');
      
      const saveData = {
        ...uncoveredLoad,
        local_accessorials: localAccessorials
      };
      const response = await loadAPI.update(id, saveData);
      const updatedLoad = response.data;
      
      // Normalize the response
      const normalizedLoad = {
        ...updatedLoad,
        stops: updatedLoad.stops || [],
        dispatch_name: updatedLoad.dispatch_name || '',
        dispatch_phone: updatedLoad.dispatch_phone || '',
        truck_lp: updatedLoad.truck_lp || '',
        truck_vin: updatedLoad.truck_vin || '',
        trailer_lp: updatedLoad.trailer_lp || '',
        covered_at: updatedLoad.covered_at || null,
        rate_con_sent_at: updatedLoad.rate_con_sent_at || null,
        carrier_line_haul: updatedLoad.carrier_line_haul || updatedLoad.carrier_pay || 0,
        customer_line_haul: updatedLoad.customer_line_haul || updatedLoad.customer_pay || 0,
        crossing_docs: updatedLoad.crossing_docs || { ccp_layout: false, cfdi: false, w9: false, doda: false, us_entry: false, redo_documents: false }
      };
      
      setLoad(normalizedLoad);
      setOriginalLoad({ ...normalizedLoad, local_accessorials: localAccessorials });
      setChangedFields({});
      toast.success('Load uncovered! Status set to Available.');
    } catch (error) {
      console.error('Uncover error:', error);
      console.error('Error response:', error.response);
      toast.error('Failed to uncover load: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleCopyDriverInfo = () => {
    const info = `Driver: ${load.driver_name || 'N/A'}, Phone: ${load.driver_phone || 'N/A'}
Truck #: ${load.truck_number || 'N/A'}, LP: ${load.truck_lp || 'N/A'}, VIN: ${load.truck_vin || 'N/A'}
Trailer #: ${load.trailer_number || 'N/A'}, LP: ${load.trailer_lp || 'N/A'}`;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(info)
        .then(() => toast.success('Driver and truck info copied to clipboard'))
        .catch(() => {
          // Fallback for non-HTTPS or permission denied
          const textarea = document.createElement('textarea');
          textarea.value = info;
          textarea.style.position = 'fixed';
          textarea.style.opacity = '0';
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
          toast.success('Driver and truck info copied to clipboard');
        });
    } else {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = info;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      toast.success('Driver and truck info copied to clipboard');
    }
  };

  // Handle appointment screenshot upload
  const handleApptScreenshotUpload = async (stopIndex, file) => {
    if (!file) return;
    
    setUploadingApptScreenshot(true);
    try {
      // Use documentAPI.upload with the correct parameters
      const response = await documentAPI.upload(
        file,                                    // file
        'load',                                  // entityType
        id,                                      // entityId (load id)
        `appt_screenshot_stop_${stopIndex + 1}`, // documentType
        `Appointment Screenshot for Stop ${stopIndex + 1}` // notes
      );
      // Construct full URL - file_url is like "/api/files/..." so we need to prepend the backend base
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const fileUrl = `${backendUrl}${response.data.file_url}`;
      
      // Update the stop with the screenshot URL
      const updatedStops = load.stops.map((stop, i) => {
        if (i === stopIndex) {
          const screenshots = stop.appt_screenshots || [];
          return { ...stop, appt_screenshots: [...screenshots, fileUrl] };
        }
        return stop;
      });
      
      setLoad({ ...load, stops: updatedStops });
      setApptScreenshotModal({ open: false, stopIndex: null });
      toast.success('Appointment screenshot uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload screenshot');
    } finally {
      setUploadingApptScreenshot(false);
    }
  };

  // Remove appointment screenshot
  const removeApptScreenshot = (stopIndex, screenshotIndex) => {
    const updatedStops = load.stops.map((stop, i) => {
      if (i === stopIndex) {
        const screenshots = [...(stop.appt_screenshots || [])];
        screenshots.splice(screenshotIndex, 1);
        return { ...stop, appt_screenshots: screenshots };
      }
      return stop;
    });
    setLoad({ ...load, stops: updatedStops });
    toast.success('Screenshot removed');
  };

  const addLocalAccessorial = () => {
    setLocalAccessorials([...localAccessorials, { name: '', carrier_amount: 0, customer_amount: 0 }]);
  };

  const updateLocalAccessorial = (index, field, value) => {
    const updated = [...localAccessorials];
    updated[index][field] = field === 'name' ? value : parseFloat(value) || 0;
    setLocalAccessorials(updated);
  };

  const removeLocalAccessorial = (index) => {
    setLocalAccessorials(localAccessorials.filter((_, i) => i !== index));
  };

  const calculateTotals = () => {
    const carrierLineHaul = parseFloat(load.carrier_line_haul) || 0;
    const customerLineHaul = parseFloat(load.customer_line_haul) || 0;
    
    const carrierAccessorialTotal = localAccessorials.reduce((sum, acc) => sum + (parseFloat(acc.carrier_amount) || 0), 0);
    const customerAccessorialTotal = localAccessorials.reduce((sum, acc) => sum + (parseFloat(acc.customer_amount) || 0), 0);

    // Fuel Surcharge — customer-side only (broker keeps it as additional margin).
    const fscAmount = load.fsc_enabled ? (parseFloat(load.fsc_amount) || 0) : 0;

    const carrierTotal = carrierLineHaul + carrierAccessorialTotal;
    const customerTotal = customerLineHaul + customerAccessorialTotal + fscAmount;
    const margin = customerTotal - carrierTotal;
    const marginPct = customerTotal > 0 ? (margin / customerTotal * 100) : 0;
    
    return { carrierTotal, customerTotal, margin, marginPct };
  };

  // Searchable options for dropdowns
  const carrierOptions = useMemo(() => {
    return carriers.map(c => ({
      value: c.id,
      label: `${c.name} - MC#${c.mc_number || 'N/A'}`,
      searchTerms: `${c.name} ${c.mc_number || ''} ${c.dot_number || ''} ${c.contact_name || ''}`
    }));
  }, [carriers]);

  const facilityOptions = useMemo(() => {
    return facilities.map(f => ({
      value: f.id,
      label: `${f.name} - ${f.city}, ${f.state}`,
      searchTerms: `${f.name} ${f.city} ${f.state} ${f.zip_code || ''} ${f.address || ''}`
    }));
  }, [facilities]);

  const customerOptions = useMemo(() => {
    return customers.map(c => ({
      value: c.id,
      label: c.name,
      searchTerms: `${c.name} ${c.mc_number || ''}`
    }));
  }, [customers]);

  const handleScheduleAppointment = (stop) => {
    const facility = facilities.find(f => f.id === stop.facility_id);
    if (!facility) return;

    if (facility.scheduling_method === 'Email' && facility.scheduling_email) {
      // Format appointment date for email subject
      let appointmentStr = '';
      if (stop.appointment_datetime) {
        const date = new Date(stop.appointment_datetime);
        appointmentStr = date.toLocaleString('en-US', { 
          month: '2-digit', 
          day: '2-digit', 
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      const poNumber = stop.po_number || 'N/A';
      const subject = encodeURIComponent(`Appointment Request - for ${poNumber} on ${appointmentStr}`);
      
      // Build mailto URL with To and CC recipients
      const toEmails = facility.scheduling_email.split(',').map(e => e.trim()).filter(e => e).join(',');
      let mailtoUrl = `mailto:${toEmails}?subject=${subject}`;
      
      // Add CC recipients if present
      if (facility.scheduling_email_cc) {
        const ccEmails = facility.scheduling_email_cc.split(',').map(e => e.trim()).filter(e => e).join(',');
        if (ccEmails) {
          mailtoUrl += `&cc=${ccEmails}`;
        }
      }
      
      window.location.href = mailtoUrl;
    } else if (facility.scheduling_method === 'Phone' && facility.scheduling_phone) {
      window.location.href = `tel:${facility.scheduling_phone}`;
    } else if (facility.scheduling_method === 'Portal' && facility.portal_url) {
      window.open(facility.portal_url, '_blank');
    } else {
      toast.info('This facility uses FCFS (First Come First Served). No scheduling needed.');
    }
  };

  const handleAddCBDocument = async () => {
    // Validate based on upload mode
    if (uploadMode === 'file') {
      if (!selectedFile) {
        toast.error('Please select a file to upload');
        return;
      }
    } else {
      if (!newDocName.trim() || !newDocUrl.trim()) {
        toast.error('Please provide document name and URL');
        return;
      }
    }
    
    setUploading(true);
    try {
      if (uploadMode === 'file') {
        // Upload file to cloud storage
        const response = await documentAPI.upload(
          selectedFile,
          'load',
          id,
          'cross_border',
          newDocNotes || null
        );
        toast.success('File uploaded successfully');
      } else {
        // Create document with URL
        await documentAPI.create({
          entity_type: 'load',
          entity_id: id,
          document_type: 'cross_border',
          file_name: newDocName,
          file_url: newDocUrl,
          notes: newDocNotes || null
        });
        toast.success('Document added successfully');
      }
      
      // Reset modal state
      setShowAddDocModal(false);
      setNewDocName('');
      setNewDocUrl('');
      setNewDocNotes('');
      setSelectedFile(null);
      setUploadMode('file');
      
      // Refresh documents
      const docsRes = await documentAPI.getByEntity('load', id);
      setCbDocuments(docsRes.data.filter(doc => doc.document_type === 'cross_border'));
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to add document');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (25MB max)
      if (file.size > 25 * 1024 * 1024) {
        toast.error('File too large. Maximum size is 25MB');
        return;
      }
      setSelectedFile(file);
      // Auto-set document name from file name
      if (!newDocName) {
        setNewDocName(file.name.split('.').slice(0, -1).join('.') || file.name);
      }
    }
  };

  const handleDeleteCBDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      await documentAPI.delete(docId);
      toast.success('Document deleted');
      setCbDocuments(cbDocuments.filter(d => d.id !== docId));
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  if (loading) {
    return <MainLayout><div className="flex items-center justify-center h-full"><div>Loading...</div></div></MainLayout>;
  }

  const totals = calculateTotals();

  return (
    <MainLayout>
      <div className="p-8" data-testid="load-detail-page">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/loads')} className="p-2 hover:bg-[var(--tms-hover)] rounded" data-testid="back-button">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-[var(--tms-text)]">
                {isNew ? 'New Load' : `Load #${load.load_number || id.substring(0, 8).toUpperCase()}`}
              </h1>
              {!isNew && load.status && <StatusBadge status={load.status} testId="load-status" />}
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="load-tabs">
          <div className="flex items-center gap-4">
            <TabsList>
              <TabsTrigger value="summary" data-testid="tab-summary" className="relative">
                Summary
                {tabHasChanges.summary && (
                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-amber-500 rounded-full animate-pulse" title="Unsaved changes" />
                )}
              </TabsTrigger>
              <TabsTrigger value="stops" data-testid="tab-stops" className="relative">
                Stops
                {tabHasChanges.stops && (
                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-amber-500 rounded-full animate-pulse" title="Unsaved changes" />
                )}
              </TabsTrigger>
              <TabsTrigger value="carrier" data-testid="tab-carrier" className="relative">
                Carrier
                {tabHasChanges.carrier && (
                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-amber-500 rounded-full animate-pulse" title="Unsaved changes" />
                )}
              </TabsTrigger>
              {load.cross_border && (
                <TabsTrigger value="cross-border" data-testid="tab-cross-border" className="relative">
                  Cross-Border
                  {tabHasChanges['cross-border'] && (
                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-amber-500 rounded-full animate-pulse" title="Unsaved changes" />
                  )}
                </TabsTrigger>
              )}
              {!isCarrierUser && (
                <TabsTrigger value="financials" data-testid="tab-financials" className="relative">
                  Financials
                  {tabHasChanges.financials && (
                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-amber-500 rounded-full animate-pulse" title="Unsaved changes" />
                  )}
                </TabsTrigger>
              )}
              {!isNew && <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>}
              {!isNew && <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>}
            </TabsList>
            
            {/* Action Buttons - Right aligned */}
            <div className="flex items-center gap-2 ml-auto">
              {!isNew && (
                <>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate(`/loads/${id}`)}
                    data-testid="edit-load-button"
                  >
                    <Pencil className="w-4 h-4 mr-1" /> Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleCopyLoad}
                    data-testid="duplicate-load-button"
                  >
                    <Copy className="w-4 h-4 mr-1" /> Duplicate
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleShareLoad}
                    data-testid="share-load-button"
                  >
                    <Share2 className="w-4 h-4 mr-1" /> Share
                  </Button>
                </>
              )}
              <Button 
                onClick={handleSave}
                disabled={!hasChanges}
                className={hasChanges 
                  ? 'bg-red-600 hover:bg-red-700 text-white' 
                  : 'bg-gray-400 text-white cursor-not-allowed'}
                data-testid="save-load-button-header"
              >
                Save Load
              </Button>
            </div>
          </div>

          {/* OVERVIEW TAB - 3-Column Dashboard Layout */}
          <TabsContent value="summary" className="mt-4">
            {/* Load Details Header - 2 Rows */}
            <div className="bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-lg p-3 mb-4 space-y-2">
              {/* Row 1: Customer, BOL#, Load#, Load Status */}
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-1">
                  <Label className="text-xs text-[var(--tms-text-secondary)] whitespace-nowrap">Customer</Label>
                  {isCarrierUser ? (
                    <span className="text-sm font-medium">{load.customer_name || '-'}</span>
                  ) : (
                    <SearchableSelect
                      options={customerOptions}
                      value={load.customer_id}
                      onValueChange={(val) => {
                        const customer = customers.find(c => c.id === val);
                        const firstOperator = customer?.operators?.[0];
                        setLoad({ 
                          ...load, 
                          customer_id: val, 
                          customer_name: customer?.name || '',
                          account_manager_id: customer?.account_manager_id || '',
                          account_manager_name: customer?.account_manager_name || '',
                          account_manager_commission_pct: customer?.account_manager_commission_pct || 0,
                          operator_id: firstOperator?.user_id || '',
                          operator_name: firstOperator?.name || '',
                          operator_commission_pct: firstOperator?.commission_pct || 0
                        });
                      }}
                      placeholder="Select customer..."
                      searchPlaceholder="Search..."
                      emptyText="No customers"
                      className="w-44"
                      data-testid="overview-select-customer"
                    />
                  )}
                </div>

                <div className="h-6 w-px bg-[var(--tms-border)]" />

                <div className="flex items-center gap-1">
                  <Label className="text-xs text-[var(--tms-text-secondary)]">BOL #</Label>
                  <Input
                    value={load.bol_number || ''}
                    onChange={(e) => setLoad({ ...load, bol_number: e.target.value })}
                    placeholder="BOL #"
                    className="h-8 w-28 text-sm font-mono"
                    data-testid="input-bol-number"
                  />
                </div>

                <div className="flex items-center gap-1">
                  <Label className="text-xs text-[var(--tms-text-secondary)]">Load #</Label>
                  <Input
                    value={load.load_number || ''}
                    onChange={(e) => setLoad({ ...load, load_number: e.target.value })}
                    placeholder="Load #"
                    className="h-8 w-28 text-sm font-mono"
                    data-testid="input-load-number"
                  />
                </div>

                <div className="h-6 w-px bg-[var(--tms-border)]" />

                <div className="flex items-center gap-1">
                  <Label className="text-xs text-[var(--tms-text-secondary)]">Status</Label>
                  {isCarrierUser ? (
                    <StatusBadge status={load.status} />
                  ) : (
                    <Select value={load.status} onValueChange={(val) => setLoad({ ...load, status: val })}>
                      <SelectTrigger className="w-36 h-8 text-sm" data-testid="overview-status">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Available">Available</SelectItem>
                        <SelectItem value="Covered">Covered</SelectItem>
                        <SelectItem value="Rate Con Sent">Rate Con Sent</SelectItem>
                        <SelectItem value="At the Shipper">At the Shipper</SelectItem>
                        {!load.cross_border && <SelectItem value="In Transit">In Transit</SelectItem>}
                        {load.cross_border && (
                          <>
                            <SelectItem value="In Transit MX">In Transit MX</SelectItem>
                            <SelectItem value="At Border MX">At Border MX</SelectItem>
                            <SelectItem value="At Border US">At Border US</SelectItem>
                            <SelectItem value="In Transit US">In Transit US</SelectItem>
                          </>
                        )}
                        <SelectItem value="At Delivery">At Delivery</SelectItem>
                        <SelectItem value="Delivered">Delivered</SelectItem>
                        <SelectItem value="Released">Released</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>

              {/* Row 2: Weight, Pallets, Equipment, Temp (if reefer), Cross Border */}
              <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-[var(--tms-border)]">
                <div className="flex items-center gap-1">
                  <Label className="text-xs text-[var(--tms-text-secondary)]">Weight</Label>
                  <Input 
                    type="number" 
                    value={load.weight || ''} 
                    onChange={(e) => setLoad({ ...load, weight: parseFloat(e.target.value) || null })} 
                    placeholder="lbs"
                    className="w-20 h-8 text-sm"
                    data-testid="overview-weight"
                  />
                </div>

                <div className="flex items-center gap-1">
                  <Label className="text-xs text-[var(--tms-text-secondary)]">Pallets</Label>
                  <Input 
                    type="number" 
                    value={load.pallet_count || ''} 
                    onChange={(e) => setLoad({ ...load, pallet_count: parseInt(e.target.value) || null })} 
                    placeholder="#"
                    className="w-16 h-8 text-sm"
                    data-testid="overview-pallets"
                  />
                </div>

                <div className="h-6 w-px bg-[var(--tms-border)]" />

                <div className="flex items-center gap-1">
                  <Label className="text-xs text-[var(--tms-text-secondary)]">Equipment</Label>
                  <Select value={load.equipment} onValueChange={(val) => setLoad({ ...load, equipment: val })}>
                    <SelectTrigger className="w-28 h-8 text-sm" data-testid="overview-equipment">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Dry Van">Dry Van</SelectItem>
                      <SelectItem value="Reefer">Reefer</SelectItem>
                      <SelectItem value="Flatbed">Flatbed</SelectItem>
                      <SelectItem value="Step Deck">Step Deck</SelectItem>
                      <SelectItem value="Conestoga">Conestoga</SelectItem>
                      <SelectItem value="Lowboy">Lowboy</SelectItem>
                      <SelectItem value="Tanker">Tanker</SelectItem>
                      <SelectItem value="Intermodal">Intermodal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Temperature Fields - Only show for Reefer */}
                {load.equipment === 'Reefer' && (
                  <>
                    <div className="flex items-center gap-1">
                      <Label className="text-xs text-[var(--tms-text-secondary)]">Temp</Label>
                      <Input 
                        value={load.temperature || ''} 
                        onChange={(e) => setLoad({ ...load, temperature: e.target.value })} 
                        placeholder="e.g. 34°F"
                        className="w-20 h-8 text-sm"
                        data-testid="overview-temperature"
                      />
                    </div>
                    <div className="flex items-center gap-1">
                      <Label className="text-xs text-[var(--tms-text-secondary)]">Mode</Label>
                      <Select value={load.reefer_mode || ''} onValueChange={(val) => setLoad({ ...load, reefer_mode: val })}>
                        <SelectTrigger className="w-24 h-8 text-sm" data-testid="overview-reefer-mode">
                          <SelectValue placeholder="Mode" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Continuous">Continuous</SelectItem>
                          <SelectItem value="Cycle">Cycle</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}

                <div className="h-6 w-px bg-[var(--tms-border)]" />

                <div className="flex items-center gap-1">
                  <Checkbox 
                    id="overview_cross_border" 
                    checked={load.cross_border} 
                    onCheckedChange={(checked) => setLoad({ ...load, cross_border: checked })} 
                    data-testid="overview-cross-border"
                  />
                  <Label htmlFor="overview_cross_border" className="text-xs cursor-pointer">XB</Label>
                </div>
              </div>
            </div>

            {/* 3-Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Column 1: Stops */}
              <div 
                className="bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-lg p-4 cursor-pointer hover:border-[var(--tms-primary)]/50 transition-colors"
                onClick={() => setActiveTab('stops')}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-[var(--tms-text)]">STOPS</h3>
                  <span className="text-xs text-[var(--tms-primary)]">Click to edit →</span>
                </div>
                <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                  {(load.stops || []).map((stop, index) => {
                    const facility = facilities.find(f => f.id === stop.facility_id);
                    const statusColors = {
                      'Needs Scheduling': 'bg-red-500',
                      'Requested': 'bg-yellow-500',
                      'Granted': 'bg-green-500',
                      'Updated TMS': 'bg-transparent border-2 border-gray-400'
                    };
                    const statusColor = statusColors[stop.appointment_status] || 'bg-gray-400';
                    
                    // Format datetime to MMDD HHMM
                    const formatDateTimeShort = (dt) => {
                      if (!dt) return '--/-- ----';
                      try {
                        const date = new Date(dt);
                        if (isNaN(date.getTime())) return '--/-- ----';
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const day = String(date.getDate()).padStart(2, '0');
                        const hours = String(date.getHours()).padStart(2, '0');
                        const minutes = String(date.getMinutes()).padStart(2, '0');
                        return `${month}/${day} ${hours}${minutes}`;
                      } catch { return '--/-- ----'; }
                    };

                    return (
                      <div 
                        key={index} 
                        className="border border-[var(--tms-border)] rounded-lg p-3 bg-[var(--tms-hover)]"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-start gap-2">
                          {/* Status Circle */}
                          <div className={`w-3 h-3 rounded-full ${statusColor} mt-1 flex-shrink-0`} title={stop.appointment_status} />
                          
                          <div className="flex-1 min-w-0">
                            {/* Stop Number & Type */}
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-semibold text-[var(--tms-text-secondary)] uppercase">
                                #{index + 1} {stop.type === 'pickup' ? 'Pickup' : 'Delivery'}
                              </span>
                              <span className="text-xs font-mono text-[var(--tms-primary)]">
                                {formatDateTimeShort(stop.appointment_datetime)}
                              </span>
                            </div>
                            
                            {/* Facility Dropdown */}
                            <SearchableSelect
                              options={facilityOptions}
                              value={stop.facility_id || ''}
                              onValueChange={(val) => {
                                updateStop(index, 'facility_id', val);
                              }}
                              placeholder="Select facility..."
                              searchPlaceholder="Search facilities..."
                              emptyText="No facilities"
                              className="mb-1"
                              data-testid={`overview-stop-facility-${index}`}
                            />
                            
                            {/* Address */}
                            <div className="text-xs text-[var(--tms-text-secondary)]">
                              {(stop.address || facility?.address) && <div className="truncate">{stop.address || facility?.address}</div>}
                              <div className="truncate">
                                {stop.city || facility?.city || '-'}, {stop.state || facility?.state || '-'} {stop.zip_code || facility?.zip_code || ''}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {(!load.stops || load.stops.length === 0) && (
                    <div className="text-center text-sm text-[var(--tms-text-muted)] py-4">
                      No stops added yet
                    </div>
                  )}
                </div>
              </div>

              {/* Column 2: Financials */}
              {!isCarrierUser && !isCustomerUser && (
                <div 
                  className="bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-lg p-4 cursor-pointer hover:border-[var(--tms-primary)]/50 transition-colors"
                  onClick={() => setActiveTab('financials')}
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-[var(--tms-text)]">FINANCIALS</h3>
                    <span className="text-xs text-[var(--tms-primary)]">Click to edit →</span>
                  </div>
                  
                  {/* Accessorials Table */}
                  <div className="overflow-hidden rounded border border-[var(--tms-border)]">
                    <table className="w-full text-xs">
                      <thead className="bg-[var(--tms-hover)]">
                        <tr>
                          <th className="text-left p-2 font-semibold">Accessorial</th>
                          <th className="text-right p-2 font-semibold">Carrier</th>
                          <th className="text-right p-2 font-semibold">Customer</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-t border-[var(--tms-border)]">
                          <td className="p-2">Line Haul</td>
                          <td className="p-2 text-right font-mono">${(load.carrier_line_haul || 0).toLocaleString()}</td>
                          <td className="p-2 text-right font-mono">${(load.customer_line_haul || 0).toLocaleString()}</td>
                        </tr>
                        {(load.accessorials || []).map((acc, idx) => (
                          <tr key={acc.id || `${acc.name}-${idx}`} className="border-t border-[var(--tms-border)]">
                            <td className="p-2">{acc.name || `Accessorial ${idx + 1}`}</td>
                            <td className="p-2 text-right font-mono">${(acc.carrier_amount || 0).toLocaleString()}</td>
                            <td className="p-2 text-right font-mono">${(acc.customer_amount || 0).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-[var(--tms-hover)] font-semibold">
                        <tr className="border-t-2 border-[var(--tms-border)]">
                          <td className="p-2">TOTALS</td>
                          <td className="p-2 text-right font-mono">${totals.carrierTotal.toLocaleString()}</td>
                          <td className="p-2 text-right font-mono">${totals.customerTotal.toLocaleString()}</td>
                        </tr>
                        <tr className="border-t border-[var(--tms-border)]">
                          <td className="p-2">MARGIN</td>
                          <td colSpan={2} className={`p-2 text-right font-mono ${totals.margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {totals.margin >= 0 ? '+' : ''}${totals.margin.toLocaleString()} ({totals.marginPct.toFixed(1)}%)
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {/* Column 3: Carrier */}
              <div className="space-y-4">
                {/* Carrier Card */}
                <div className="bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-[var(--tms-text)] mb-3">CARRIER</h3>
                  
                  {/* Carrier Dropdown */}
                  {!isCarrierUser && (
                    <div className="mb-3">
                      <SearchableSelect
                        options={carrierOptions}
                        value={load.carrier_id || ''}
                        onValueChange={handleCarrierChange}
                        placeholder="Select carrier..."
                        searchPlaceholder="Search by name, MC#..."
                        emptyText="No carriers"
                        data-testid="overview-select-carrier"
                      />
                    </div>
                  )}

                  {load.carrier_id ? (
                    <div className="space-y-2 text-sm">
                      <div>
                        <p className="font-medium text-[var(--tms-text)]">{load.carrier_name}</p>
                        {(() => {
                          const carr = carriers.find(c => c.id === load.carrier_id);
                          return carr?.mc_number ? (
                            <p className="text-xs text-[var(--tms-text-secondary)]">MC# {carr.mc_number}</p>
                          ) : null;
                        })()}
                      </div>
                      
                      <div className="pt-2 border-t border-[var(--tms-border)] space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs text-[var(--tms-text-muted)]">Driver</Label>
                            <Input 
                              value={load.driver_name || ''} 
                              onChange={(e) => setLoad({ ...load, driver_name: e.target.value })}
                              placeholder="Name"
                              className="h-8 text-sm"
                              data-testid="overview-driver-name"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-[var(--tms-text-muted)]">Phone</Label>
                            <Input 
                              value={load.driver_phone || ''} 
                              onChange={(e) => setLoad({ ...load, driver_phone: e.target.value })}
                              placeholder="Phone"
                              className="h-8 text-sm"
                              data-testid="overview-driver-phone"
                            />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs text-[var(--tms-text-muted)]">Truck #</Label>
                            <Input 
                              value={load.truck_number || ''} 
                              onChange={(e) => setLoad({ ...load, truck_number: e.target.value })}
                              placeholder="Truck #"
                              className="h-8 text-sm"
                              data-testid="overview-truck-number"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-[var(--tms-text-muted)]">Trailer #</Label>
                            <Input 
                              value={load.trailer_number || ''} 
                              onChange={(e) => setLoad({ ...load, trailer_number: e.target.value })}
                              placeholder="Trailer #"
                              className="h-8 text-sm"
                              data-testid="overview-trailer-number"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <Label className="text-xs text-[var(--tms-text-muted)]">Truck VIN</Label>
                          <Input 
                            value={load.truck_vin || ''} 
                            onChange={(e) => setLoad({ ...load, truck_vin: e.target.value })}
                            placeholder="VIN"
                            className="h-8 text-sm font-mono"
                            data-testid="overview-truck-vin"
                          />
                        </div>
                        
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={handleCopyDriverInfo}
                          className="w-full mt-2"
                          data-testid="overview-copy-driver-info"
                        >
                          <Copy className="w-3 h-3 mr-1" /> Copy Driver & Truck Info
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-[var(--tms-text-muted)]">No carrier assigned</p>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Add Note Section */}
            {!isNew && (
              <div className="mt-4 bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-lg p-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Add a quick note..."
                    value={newNoteContent}
                    onChange={(e) => setNewNoteContent(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && newNoteContent.trim()) {
                        interactionAPI.create({ 
                          entity_type: 'load', 
                          entity_id: id, 
                          interaction_type: 'note', 
                          subject: 'Quick Note', 
                          content: newNoteContent.trim(), 
                          attachments: [] 
                        })
                        .then(() => { 
                          toast.success('Note added'); 
                          fetchLoadData(); 
                          setNewNoteContent('');
                        })
                        .catch(() => toast.error('Failed to add note'));
                      }
                    }}
                    className="flex-1"
                    data-testid="overview-quick-note"
                  />
                  <Button
                    onClick={() => {
                      if (newNoteContent.trim()) {
                        interactionAPI.create({ 
                          entity_type: 'load', 
                          entity_id: id, 
                          interaction_type: 'note', 
                          subject: 'Quick Note', 
                          content: newNoteContent.trim(), 
                          attachments: [] 
                        })
                        .then(() => { 
                          toast.success('Note added'); 
                          fetchLoadData(); 
                          setNewNoteContent('');
                        })
                        .catch(() => toast.error('Failed to add note'));
                      }
                    }}
                    disabled={!newNoteContent.trim()}
                    data-testid="overview-add-note-btn"
                  >
                    Add Note
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* STOPS TAB - With reordering and type change */}
          <TabsContent value="stops" className="mt-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              {!isCarrierUser && (
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => addStop('pickup')} data-testid="add-pickup-button">
                    <Plus className="w-3 h-3 mr-1" /> Add Pickup
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => addStop('delivery')} data-testid="add-delivery-button">
                    <Plus className="w-3 h-3 mr-1" /> Add Delivery
                  </Button>
                </div>
              )}
              {!isCarrierUser && <p className="text-xs text-[var(--tms-text-muted)]">First stop = Pickup, Last stop = Delivery</p>}
            </div>
            
            {/* Carrier Read-Only View */}
            {isCarrierUser ? (
              <div className="space-y-3">
                {load.stops.map((stop, index) => {
                  const facility = facilities.find(f => f.id === stop.facility_id);
                  const isPickup = stop.type === 'pickup';
                  return (
                    <div key={index} className={`bg-[var(--tms-card)] border-2 rounded-lg p-3 ${isPickup ? 'border-blue-400/50' : 'border-green-400/50'}`} data-testid={`stop-${index}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-bold text-white ${isPickup ? 'bg-blue-500' : 'bg-green-500'}`}>#{index + 1} {isPickup ? 'P' : 'D'}</span>
                        <AppointmentBadge status={stop.appointment_status} />
                        <span className="text-xs text-[var(--tms-text-secondary)]">{stop.appointment_status}</span>
                        <span className="text-xs font-mono ml-auto">{stop.po_number || ''}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-3 text-sm">
                        <div>
                          <p className="font-medium">{stop.facility_name || 'Not set'}</p>
                          <p className="text-xs text-[var(--tms-text-muted)]">{facility?.address || stop.address || ''}</p>
                          <p className="text-xs text-[var(--tms-text-muted)]">{stop.city || ''}, {stop.state || ''} {stop.zip_code || ''}</p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs"><span className="text-[var(--tms-text-muted)]">Appt:</span> {stop.appointment_datetime ? new Date(stop.appointment_datetime).toLocaleString() : '--'}</p>
                          <p className="text-xs"><span className="text-[var(--tms-text-muted)]">Arrival:</span> {stop.actual_arrival ? new Date(stop.actual_arrival).toLocaleString() : '--'}</p>
                          <p className="text-xs"><span className="text-[var(--tms-text-muted)]">Departed:</span> {stop.actual_departure ? new Date(stop.actual_departure).toLocaleString() : '--'}</p>
                        </div>
                        <div>
                          {stop.notes && <p className="text-xs text-[var(--tms-text-secondary)]">{stop.notes}</p>}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {load.stops.length === 0 && <p className="text-[var(--tms-text-muted)] text-center py-8">No stops configured</p>}
              </div>
            ) : (
              /* Editable Compact Grid View */
              <div className="space-y-3">
                {load.stops.map((stop, index) => {
                  const facility = facilities.find(f => f.id === stop.facility_id);
                  const isFirst = index === 0;
                  const isLast = index === load.stops.length - 1;
                  const isPickup = stop.type === 'pickup';
                  const screenshots = stop.appt_screenshots || [];
                  
                  return (
                    <div key={index} className={`bg-[var(--tms-card)] border-2 rounded-lg ${isPickup ? 'border-blue-400/40' : 'border-green-400/40'}`} data-testid={`stop-${index}`}>
                      
                      {/* Row 0: Header Bar - colored by appointment status */}
                      <div className={`flex items-center gap-2 px-3 py-2 border-b ${
                        stop.appointment_status === 'Needs Scheduling' ? 'bg-red-500/10 border-red-300' :
                        stop.appointment_status === 'Requested' ? 'bg-yellow-400/15 border-yellow-400' :
                        stop.appointment_status === 'Granted' ? 'bg-green-500/10 border-green-300' :
                        'bg-gray-100 border-gray-300'
                      }`}>
                        <span className={`px-2 py-0.5 rounded text-xs font-bold text-white ${isPickup ? 'bg-blue-500' : 'bg-green-500'}`}>
                          #{stop.stop_number || index + 1}
                        </span>
                        <Select value={stop.type} onValueChange={(val) => updateStop(index, 'type', val)}>
                          <SelectTrigger className={`w-28 h-7 text-xs font-semibold ${isPickup ? 'border-blue-300' : 'border-green-300'}`} data-testid={`select-stop-type-${index}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pickup">PICKUP</SelectItem>
                            <SelectItem value="delivery">DELIVERY</SelectItem>
                          </SelectContent>
                        </Select>
                        <AppointmentBadge status={stop.appointment_status} />
                        <Select value={stop.appointment_status} onValueChange={(val) => {
                          updateStop(index, 'appointment_status', val);
                          if (apptDateTimeErrors[index]) { setApptDateTimeErrors(prev => { const n = { ...prev }; delete n[index]; return n; }); }
                        }}>
                          <SelectTrigger className="w-36 h-7 text-xs" data-testid={`select-appointment-${index}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Needs Scheduling">Needs Scheduling</SelectItem>
                            <SelectItem value="Requested">Requested</SelectItem>
                            <SelectItem value="Granted">Granted</SelectItem>
                            <SelectItem value="Updated TMS">Updated TMS</SelectItem>
                          </SelectContent>
                        </Select>
                        
                        <div className="ml-auto flex items-center gap-1">
                          {screenshots.length > 0 && (
                            <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">{screenshots.length} img</span>
                          )}
                          <button onClick={() => setApptScreenshotModal({ open: true, stopIndex: index })} className="p-1 hover:bg-[var(--tms-hover)] rounded" title="Appt Screenshot" data-testid={`appt-screenshot-button-${index}`}>
                            <Camera className="w-3.5 h-3.5 text-[var(--tms-text-secondary)]" />
                          </button>
                          <button onClick={() => moveStop(index, 'up')} disabled={isFirst} className={`p-1 rounded ${isFirst ? 'opacity-30' : 'hover:bg-[var(--tms-hover)]'}`} data-testid={`move-up-${index}`}>
                            <ArrowUp className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => moveStop(index, 'down')} disabled={isLast} className={`p-1 rounded ${isLast ? 'opacity-30' : 'hover:bg-[var(--tms-hover)]'}`} data-testid={`move-down-${index}`}>
                            <ArrowDown className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => removeStop(index)} className="p-1 text-red-500 hover:bg-red-50 rounded" data-testid={`remove-stop-${index}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      
                      {/* 3-Column Grid Body */}
                      <div className="px-3 py-2">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                          {/* Column 1: Facility */}
                          <div className="space-y-2">
                            <div>
                              <Label className="text-xs text-[var(--tms-text-muted)]">Facility</Label>
                              <SearchableSelect
                                options={facilityOptions}
                                value={stop.facility_id}
                                onValueChange={(val) => updateStop(index, 'facility_id', val)}
                                placeholder="Search facility..."
                                searchPlaceholder="Name, city, zip..."
                                emptyText="No facilities"
                                data-testid={`select-facility-${index}`}
                              />
                            </div>
                            <div className="text-xs text-[var(--tms-text-secondary)] bg-[var(--tms-hover)] rounded px-2 py-1.5 min-h-[2rem]">
                              {(stop.address || facility?.address) ? (
                                <>
                                  <div>{stop.address || facility?.address}</div>
                                  <div>{stop.city || facility?.city || ''}, {stop.state || facility?.state || ''} {stop.zip_code || facility?.zip_code || ''}</div>
                                </>
                              ) : (
                                <span className="text-[var(--tms-text-muted)]">Select facility above</span>
                              )}
                            </div>
                          </div>

                          {/* Column 2: Appointment & Times */}
                          <div className="space-y-2">
                            <div>
                              <Label className={`text-xs ${apptDateTimeErrors[index] ? 'text-red-500' : 'text-[var(--tms-text-muted)]'}`}>
                                Appt Date/Time{['Requested','Granted','Updated TMS'].includes(stop.appointment_status) && <span className="text-red-500"> *</span>}
                              </Label>
                              <DateTimeInput 
                                value={stop.appointment_datetime || ''} 
                                onChange={(e) => {
                                  updateStop(index, 'appointment_datetime', e.target.value);
                                  if (e.target.value && apptDateTimeErrors[index]) { setApptDateTimeErrors(prev => { const n = { ...prev }; delete n[index]; return n; }); }
                                }} 
                                className={`h-8 text-sm ${apptDateTimeErrors[index] ? 'border-red-500 border-2 bg-red-50' : ''}`}
                                placeholder="MMDDHHMM"
                                data-testid={`input-datetime-${index}`} 
                              />
                              {apptDateTimeErrors[index] && <p className="text-xs text-red-500">Required for {stop.appointment_status}</p>}
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-xs text-[var(--tms-text-muted)]">Arrival</Label>
                                <DateTimeInput value={stop.actual_arrival || ''} onChange={(e) => updateStop(index, 'actual_arrival', e.target.value)} className="h-8 text-sm" placeholder="MMDDHHMM" data-testid={`input-actual-arrival-${index}`} />
                              </div>
                              <div>
                                <Label className="text-xs text-[var(--tms-text-muted)]">Departure</Label>
                                <DateTimeInput value={stop.actual_departure || ''} onChange={(e) => updateStop(index, 'actual_departure', e.target.value)} className="h-8 text-sm" placeholder="MMDDHHMM" data-testid={`input-actual-departure-${index}`} />
                              </div>
                            </div>
                          </div>

                          {/* Column 3: References */}
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <Label className="text-xs text-[var(--tms-text-muted)]">Reference #</Label>
                              <button
                                onClick={() => {
                                  const refs = [...(stop.references || []), ''];
                                  updateStop(index, 'references', refs);
                                }}
                                className="text-xs text-[var(--tms-primary)] hover:underline flex items-center gap-0.5"
                                data-testid={`add-ref-${index}`}
                              >
                                <Plus className="w-3 h-3" /> Add
                              </button>
                            </div>
                            <div className="space-y-1.5 max-h-32 overflow-y-auto">
                              {(stop.references && stop.references.length > 0) ? (
                                stop.references.map((ref, refIdx) => (
                                  <div key={refIdx} className="flex items-center gap-1">
                                    <Input
                                      value={ref}
                                      onChange={(e) => {
                                        const refs = [...(stop.references || [])];
                                        refs[refIdx] = e.target.value;
                                        updateStop(index, 'references', refs);
                                      }}
                                      className="h-7 text-xs font-mono"
                                      placeholder={`Ref #${refIdx + 1}`}
                                      data-testid={`input-ref-${index}-${refIdx}`}
                                    />
                                    <button
                                      onClick={() => {
                                        const refs = (stop.references || []).filter((_, i) => i !== refIdx);
                                        updateStop(index, 'references', refs);
                                      }}
                                      className="p-0.5 text-red-400 hover:text-red-600 flex-shrink-0"
                                      data-testid={`remove-ref-${index}-${refIdx}`}
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                ))
                              ) : (
                                <p className="text-xs text-[var(--tms-text-muted)] py-1">No references added</p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Bottom Row: Notes + Screenshots + Actions */}
                        <div className="mt-2 pt-2 border-t border-[var(--tms-border)] flex items-center gap-2 flex-wrap">
                          <div className="flex-1 min-w-[200px]">
                            <Input value={stop.notes || ''} onChange={(e) => updateStop(index, 'notes', e.target.value)} className="h-7 text-xs" placeholder="Stop notes..." data-testid={`input-stop-notes-${index}`} />
                          </div>
                          {screenshots.length > 0 && (
                            <div className="flex gap-1">
                              {screenshots.map((url, ssIdx) => (
                                <div key={ssIdx} className="relative group">
                                  <img src={url} alt={`SS ${ssIdx + 1}`} className="w-8 h-8 object-cover rounded border cursor-pointer hover:opacity-80" onClick={() => setApptImagePreview({ open: true, imageUrl: url, stopIndex: index })} />
                                  <button onClick={() => removeApptScreenshot(index, ssIdx)} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-2 h-2" /></button>
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="flex gap-1">
                            {facility && facility.scheduling_method !== 'FCFS' && (
                              <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={() => handleScheduleAppointment(stop)} data-testid={`schedule-button-${index}`}>
                                {facility.scheduling_method === 'Email' && <Mail className="w-3 h-3 mr-1" />}
                                {facility.scheduling_method === 'Phone' && <Phone className="w-3 h-3 mr-1" />}
                                {facility.scheduling_method === 'Portal' && <Globe className="w-3 h-3 mr-1" />}
                                {facility.scheduling_method}
                              </Button>
                            )}
                            {facility && (
                              <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={() => navigate(`/facilities/${stop.facility_id}`)} data-testid={`facility-button-${index}`}>
                                <Building2 className="w-3 h-3 mr-1" /> Facility
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {load.stops.length === 0 && <p className="text-[var(--tms-text-muted)] text-center py-8">No stops added yet. Add pickup and delivery stops.</p>}
              </div>
            )}
          </TabsContent>

          {/* CARRIER TAB - 2-Column Layout */}
          <TabsContent value="carrier" className="mt-4">
            {/* Top Section: Carrier Assignment + Coverage Status */}
            <div className="bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-[var(--tms-text)]">CARRIER ASSIGNMENT</h3>
                {load.carrier_id && !isCarrierUser && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleUncover}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-300"
                    data-testid="uncover-button"
                  >
                    <UserX className="w-3 h-3 mr-1" />
                    Uncover
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-[var(--tms-text-muted)]">Carrier</Label>
                  {!isCarrierUser ? (
                    <SearchableSelect
                      options={carrierOptions}
                      value={load.carrier_id || ''}
                      onValueChange={handleCarrierChange}
                      placeholder="Search carrier by name, MC#..."
                      searchPlaceholder="Type to search carriers..."
                      emptyText="No carriers found"
                      data-testid="select-carrier"
                      className={changedFields['carrier_id'] ? getFieldClass('carrier_id') : ''}
                    />
                  ) : (
                    <ReadOnlyField value={load.carrier_name} placeholder="Not assigned" />
                  )}
                </div>
                {!isCarrierUser && (
                  <div>
                    <Label className="text-xs text-[var(--tms-text-muted)]">Coverage Status</Label>
                    <Select 
                      value={
                        load.rate_con_sent_at ? 'rate_con_sent' : 
                        load.covered_at ? 'covered' : 
                        'not_covered'
                      } 
                      onValueChange={(val) => {
                        if (val === 'not_covered') {
                          handleStatusCheckbox('covered', false);
                        } else if (val === 'covered') {
                          handleStatusCheckbox('covered', true);
                          setLoad(prev => ({ ...prev, rate_con_sent_at: null }));
                        } else if (val === 'rate_con_sent') {
                          if (!load.covered_at) handleStatusCheckbox('covered', true);
                          handleStatusCheckbox('rate_con_sent', true);
                        }
                      }}
                    >
                      <SelectTrigger className="h-9 text-sm" data-testid="select-coverage-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="not_covered">Not Covered</SelectItem>
                        <SelectItem value="covered">Covered</SelectItem>
                        <SelectItem value="rate_con_sent">Rate Con Sent</SelectItem>
                      </SelectContent>
                    </Select>
                    {(load.covered_at || load.rate_con_sent_at) && (
                      <div className="mt-1 space-y-0.5">
                        {load.covered_at && (
                          <p className="text-xs text-[var(--tms-text-muted)]">Covered: {new Date(load.covered_at).toLocaleString()}</p>
                        )}
                        {load.rate_con_sent_at && (
                          <p className="text-xs text-[var(--tms-text-muted)]">Rate Con: {new Date(load.rate_con_sent_at).toLocaleString()}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* 3-Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Left Column: Driver & Dispatch */}
              <div className="bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-lg p-4">
                <h3 className="text-sm font-semibold text-[var(--tms-text)] mb-3">DRIVER & DISPATCH</h3>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-[var(--tms-text-muted)]">Driver Name</Label>
                    <Input 
                      value={load.driver_name || ''} 
                      onChange={(e) => setLoad({ ...load, driver_name: e.target.value })} 
                      className={`h-8 text-sm ${getFieldClass('driver_name')}`}
                      placeholder="Driver name"
                      data-testid="input-driver-name" 
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[var(--tms-text-muted)]">Driver Phone</Label>
                    <Input 
                      value={load.driver_phone || ''} 
                      onChange={(e) => setLoad({ ...load, driver_phone: e.target.value })} 
                      className={`h-8 text-sm ${getFieldClass('driver_phone')}`}
                      placeholder="Phone #"
                      data-testid="input-driver-phone" 
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[var(--tms-text-muted)]">Dispatch Name</Label>
                    <Input 
                      value={load.dispatch_name || ''} 
                      onChange={(e) => setLoad({ ...load, dispatch_name: e.target.value })} 
                      className={`h-8 text-sm ${getFieldClass('dispatch_name')}`}
                      placeholder="Dispatch name"
                      data-testid="input-dispatch-name" 
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[var(--tms-text-muted)]">Dispatch Phone</Label>
                    <Input 
                      value={load.dispatch_phone || ''} 
                      onChange={(e) => setLoad({ ...load, dispatch_phone: e.target.value })} 
                      className={`h-8 text-sm ${getFieldClass('dispatch_phone')}`}
                      placeholder="Phone #"
                      data-testid="input-dispatch-phone" 
                    />
                  </div>
                </div>
              </div>

              {/* Middle Column: Truck & Trailer */}
              <div className="bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-lg p-4">
                <h3 className="text-sm font-semibold text-[var(--tms-text)] mb-3">TRUCK & TRAILER</h3>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-[var(--tms-text-muted)]">Truck #</Label>
                    <Input 
                      value={load.truck_number || ''} 
                      onChange={(e) => setLoad({ ...load, truck_number: e.target.value })} 
                      className={`h-8 text-sm ${getFieldClass('truck_number')}`}
                      placeholder="Truck #"
                      data-testid="input-truck-number" 
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[var(--tms-text-muted)]">Truck LP</Label>
                    <Input 
                      value={load.truck_lp || ''} 
                      onChange={(e) => setLoad({ ...load, truck_lp: e.target.value })} 
                      className={`h-8 text-sm ${getFieldClass('truck_lp')}`}
                      placeholder="License plate"
                      data-testid="input-truck-lp" 
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[var(--tms-text-muted)]">Truck VIN</Label>
                    <Input 
                      value={load.truck_vin || ''} 
                      onChange={(e) => setLoad({ ...load, truck_vin: e.target.value })} 
                      className={`h-8 text-sm font-mono ${getFieldClass('truck_vin')}`}
                      placeholder="VIN"
                      data-testid="input-truck-vin" 
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[var(--tms-text-muted)]">Trailer #</Label>
                    <Input 
                      value={load.trailer_number || ''} 
                      onChange={(e) => setLoad({ ...load, trailer_number: e.target.value })} 
                      className={`h-8 text-sm ${getFieldClass('trailer_number')}`}
                      placeholder="Trailer #"
                      data-testid="input-trailer-number" 
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[var(--tms-text-muted)]">Trailer LP</Label>
                    <Input 
                      value={load.trailer_lp || ''} 
                      onChange={(e) => setLoad({ ...load, trailer_lp: e.target.value })} 
                      className={`h-8 text-sm ${getFieldClass('trailer_lp')}`}
                      placeholder="License plate"
                      data-testid="input-trailer-lp" 
                    />
                  </div>
                </div>
              </div>

              {/* Right Column: Drop Off Trailer */}
              <div className="bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Checkbox 
                    id="drop_off_trailer" 
                    checked={load.drop_off_trailer} 
                    onCheckedChange={(checked) => setLoad({ 
                      ...load, 
                      drop_off_trailer: checked,
                      trailer_dropped_date: checked ? load.trailer_dropped_date : null,
                      trailer_ready_for_pickup_date: checked ? load.trailer_ready_for_pickup_date : null,
                      trailer_pulled_date: checked ? load.trailer_pulled_date : null
                    })} 
                    disabled={isCarrierUser}
                    data-testid="checkbox-drop-off-trailer" 
                  />
                  <Label htmlFor="drop_off_trailer" className="text-sm font-semibold text-[var(--tms-text)] cursor-pointer">DROP OFF TRAILER</Label>
                </div>
                {load.drop_off_trailer ? (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs text-[var(--tms-text-muted)]">Dropped Date</Label>
                      {isCarrierUser ? (
                        <ReadOnlyField value={load.trailer_dropped_date} />
                      ) : (
                        <DateInput
                          value={load.trailer_dropped_date || ''}
                          onChange={(e) => setLoad({ ...load, trailer_dropped_date: e.target.value || null })}
                          className={`h-8 text-sm ${getFieldClass('trailer_dropped_date')}`}
                          data-testid="input-trailer-dropped-date"
                        />
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-[var(--tms-text-muted)]">Ready for Pickup</Label>
                      {isCarrierUser ? (
                        <ReadOnlyField value={load.trailer_ready_for_pickup_date} />
                      ) : (
                        <DateInput
                          value={load.trailer_ready_for_pickup_date || ''}
                          onChange={(e) => setLoad({ ...load, trailer_ready_for_pickup_date: e.target.value || null })}
                          className={`h-8 text-sm ${getFieldClass('trailer_ready_for_pickup_date')}`}
                          data-testid="input-trailer-ready-for-pickup-date"
                        />
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-[var(--tms-text-muted)]">Pulled Date</Label>
                      {isCarrierUser ? (
                        <ReadOnlyField value={load.trailer_pulled_date} />
                      ) : (
                        <DateInput
                          value={load.trailer_pulled_date || ''}
                          onChange={(e) => setLoad({ ...load, trailer_pulled_date: e.target.value || null })}
                          className={`h-8 text-sm ${getFieldClass('trailer_pulled_date')}`}
                          data-testid="input-trailer-pulled-date"
                        />
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-[var(--tms-text-muted)]">Check the box to enable drop trailer tracking</p>
                )}
              </div>
            </div>

            {/* Copy Button */}
            <div className="mt-4">
              <Button variant="outline" size="sm" onClick={handleCopyDriverInfo} data-testid="copy-driver-info-button">
                <Copy className="w-4 h-4 mr-1" /> Copy Driver & Truck Info
              </Button>
            </div>
          </TabsContent>

          {load.cross_border && (
            <TabsContent value="cross-border" className="mt-4">
              <CrossBorderTab
                load={load}
                setLoad={setLoad}
                getFieldClass={getFieldClass}
                isCarrierUser={isCarrierUser}
                cbDocuments={cbDocuments}
                setShowAddDocModal={setShowAddDocModal}
                handleDeleteCBDocument={handleDeleteCBDocument}
                fetchLoadData={fetchLoadData}
              />
            </TabsContent>
          )}

          {/* FINANCIALS TAB - 2-Column with Table for Rates */}
          <TabsContent value="financials" className="mt-4">
            <FinancialsTab
              load={load}
              setLoad={setLoad}
              totals={totals}
              fscAutoInfo={fscAutoInfo}
              fscAutoLoading={fscAutoLoading}
              fscManualOverride={fscManualOverride}
              setFscManualOverride={setFscManualOverride}
              localAccessorials={localAccessorials}
              addLocalAccessorial={addLocalAccessorial}
              updateLocalAccessorial={updateLocalAccessorial}
              removeLocalAccessorial={removeLocalAccessorial}
              getFieldClass={getFieldClass}
              changedFields={changedFields}
              showCommission={showCommission}
              canEditAMOP={canEditAMOP}
              staffUsers={staffUsers}
            />
          </TabsContent>

          {!isNew && (
            <TabsContent value="notes" className="mt-4">
              <NotesTab
                id={id}
                interactions={interactions}
                showNoteModal={showNoteModal}
                setShowNoteModal={setShowNoteModal}
                newNoteContent={newNoteContent}
                setNewNoteContent={setNewNoteContent}
                fetchLoadData={fetchLoadData}
              />
            </TabsContent>
          )}

          {/* Documents Tab - BOL Generation - forceMount keeps it mounted for real-time sync */}
          {!isNew && (
            <TabsContent value="documents" className="mt-4" forceMount>
              <BOLGenerator 
                loadId={id}
                load={load}
                facilities={facilities}
                carriers={carriers}
              />
            </TabsContent>
          )}
        </Tabs>

        {/* Add Cross-Border Document Modal */}
        {showAddDocModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { if (!uploading) setShowAddDocModal(false); }}>
            <div className="bg-[var(--tms-card)] rounded-lg p-6 w-full max-w-lg mx-4" onClick={(e) => e.stopPropagation()}>
              <h4 className="text-lg font-semibold mb-4">Add Cross-Border Document</h4>
              
              {/* Upload Mode Toggle */}
              <div className="flex gap-2 mb-4 p-1 bg-[var(--tms-hover)] rounded-lg">
                <button
                  onClick={() => setUploadMode('file')}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    uploadMode === 'file' 
                      ? 'bg-[var(--tms-card)] text-[var(--tms-text)] shadow-sm' 
                      : 'text-[var(--tms-text-secondary)] hover:text-[var(--tms-text)]'
                  }`}
                  data-testid="upload-mode-file"
                >
                  <Upload className="w-4 h-4 inline mr-2" />
                  Upload File
                </button>
                <button
                  onClick={() => setUploadMode('url')}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    uploadMode === 'url' 
                      ? 'bg-[var(--tms-card)] text-[var(--tms-text)] shadow-sm' 
                      : 'text-[var(--tms-text-secondary)] hover:text-[var(--tms-text)]'
                  }`}
                  data-testid="upload-mode-url"
                >
                  <Globe className="w-4 h-4 inline mr-2" />
                  Enter URL
                </button>
              </div>

              <div className="space-y-4">
                {uploadMode === 'file' ? (
                  <>
                    {/* File Upload Area */}
                    <div className="space-y-2">
                      <Label>Select File *</Label>
                      <div className="border-2 border-dashed border-[var(--tms-border)] rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                        <input
                          type="file"
                          id="fileUpload"
                          className="hidden"
                          onChange={handleFileSelect}
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.xml,.csv,.txt,.png,.jpg,.jpeg,.gif,.webp,.bmp,.tiff"
                          data-testid="file-input"
                        />
                        <label htmlFor="fileUpload" className="cursor-pointer">
                          {selectedFile ? (
                            <div className="space-y-2">
                              <FileText className="w-12 h-12 text-blue-500 mx-auto" />
                              <p className="font-medium text-[var(--tms-text)]">{selectedFile.name}</p>
                              <p className="text-sm text-[var(--tms-text-muted)]">
                                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                              </p>
                              <p className="text-xs text-[var(--tms-primary)]">Click to change file</p>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <Upload className="w-12 h-12 text-[var(--tms-text-muted)] mx-auto" />
                              <p className="font-medium text-[var(--tms-text-secondary)]">Click to select a file</p>
                              <p className="text-sm text-[var(--tms-text-muted)]">
                                PDF, DOC, DOCX, XLS, XLSX, PNG, JPG, etc. (Max 25MB)
                              </p>
                            </div>
                          )}
                        </label>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* URL Input Mode */}
                    <div className="space-y-2">
                      <Label htmlFor="docName">Document Name *</Label>
                      <Input
                        id="docName"
                        placeholder="e.g., Customs Declaration Form"
                        value={newDocName}
                        onChange={(e) => setNewDocName(e.target.value)}
                        data-testid="doc-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="docUrl">Document URL *</Label>
                      <Input
                        id="docUrl"
                        placeholder="https://..."
                        value={newDocUrl}
                        onChange={(e) => setNewDocUrl(e.target.value)}
                        data-testid="doc-url-input"
                      />
                      <p className="text-xs text-[var(--tms-text-muted)]">Enter the URL where the document is hosted (Google Drive, Dropbox, etc.)</p>
                    </div>
                  </>
                )}
                
                {/* Notes field (shared) */}
                <div className="space-y-2">
                  <Label htmlFor="docNotes">Notes (optional)</Label>
                  <Textarea
                    id="docNotes"
                    placeholder="Additional notes about this document..."
                    value={newDocNotes}
                    onChange={(e) => setNewDocNotes(e.target.value)}
                    rows={2}
                    data-testid="doc-notes-input"
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-2 mt-6">
                <Button 
                  variant="outline" 
                  onClick={() => { 
                    setShowAddDocModal(false); 
                    setNewDocName(''); 
                    setNewDocUrl(''); 
                    setNewDocNotes('');
                    setSelectedFile(null);
                    setUploadMode('file');
                  }}
                  disabled={uploading}
                  data-testid="cancel-doc-button"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleAddCBDocument}
                  disabled={uploading || (uploadMode === 'file' ? !selectedFile : (!newDocName.trim() || !newDocUrl.trim()))}
                  data-testid="save-doc-button"
                >
                  {uploading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-1" />
                      {uploadMode === 'file' ? 'Upload File' : 'Add Document'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Appointment Screenshot Upload Modal */}
        <Dialog open={apptScreenshotModal.open} onOpenChange={(open) => { setApptScreenshotModal({ open, stopIndex: open ? apptScreenshotModal.stopIndex : null }); setIsDraggingAppt(false); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Camera className="w-5 h-5 text-[var(--tms-warning)]" />
                Upload Appointment Screenshot
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-[var(--tms-text-secondary)]">
                Upload a screenshot of the appointment confirmation for Stop #{apptScreenshotModal.stopIndex !== null ? apptScreenshotModal.stopIndex + 1 : ''}.
              </p>
              <div 
                className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 cursor-pointer ${
                  isDraggingAppt 
                    ? 'border-[var(--tms-warning)] bg-[var(--tms-warning)]/20 scale-[1.02]' 
                    : 'border-[var(--tms-warning)]/50 bg-[var(--tms-warning)]/10 hover:border-[var(--tms-warning)] hover:bg-[var(--tms-warning)]/15'
                }`}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingAppt(true); }}
                onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingAppt(true); }}
                onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingAppt(false); }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDraggingAppt(false);
                  const files = e.dataTransfer?.files;
                  if (files && files.length > 0 && apptScreenshotModal.stopIndex !== null) {
                    const file = files[0];
                    if (file.type.startsWith('image/')) {
                      handleApptScreenshotUpload(apptScreenshotModal.stopIndex, file);
                    } else {
                      toast.error('Please drop an image file (PNG, JPG, JPEG, GIF)');
                    }
                  }
                }}
                onClick={() => document.getElementById('apptScreenshotInput')?.click()}
                data-testid="appt-screenshot-dropzone"
              >
                <input
                  type="file"
                  id="apptScreenshotInput"
                  className="hidden"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file && apptScreenshotModal.stopIndex !== null) {
                      handleApptScreenshotUpload(apptScreenshotModal.stopIndex, file);
                    }
                    // Reset input so same file can be selected again
                    e.target.value = '';
                  }}
                  data-testid="appt-screenshot-input"
                />
                {uploadingApptScreenshot ? (
                  <div className="space-y-3">
                    <div className="w-14 h-14 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto" />
                    <p className="font-medium text-amber-700">Uploading...</p>
                  </div>
                ) : isDraggingAppt ? (
                  <div className="space-y-3">
                    <Upload className="w-14 h-14 text-[var(--tms-warning)] mx-auto animate-bounce" />
                    <p className="font-semibold text-amber-700 text-lg">Drop your image here!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex justify-center gap-4">
                      <Camera className="w-10 h-10 text-amber-500" />
                      <Upload className="w-10 h-10 text-amber-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-amber-700 text-lg">
                        Drag & drop your image here
                      </p>
                      <p className="text-[var(--tms-warning)] mt-1">or</p>
                      <p className="font-medium text-amber-700 mt-1 underline">
                        Click to browse files
                      </p>
                    </div>
                    <p className="text-sm text-[var(--tms-text-muted)] mt-2">
                      Supports: PNG, JPG, JPEG, GIF (Max 10MB)
                    </p>
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Appointment Screenshot Preview Modal */}
        <Dialog open={apptImagePreview.open} onOpenChange={(open) => setApptImagePreview({ open, imageUrl: open ? apptImagePreview.imageUrl : '', stopIndex: open ? apptImagePreview.stopIndex : null })}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Image className="w-5 h-5 text-[var(--tms-warning)]" />
                Appointment Screenshot - Stop #{apptImagePreview.stopIndex !== null ? apptImagePreview.stopIndex + 1 : ''}
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              {apptImagePreview.imageUrl && (
                <img 
                  src={apptImagePreview.imageUrl} 
                  alt="Appointment Screenshot" 
                  className="w-full h-auto rounded-lg border shadow-lg"
                />
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button 
                variant="outline" 
                onClick={() => window.open(apptImagePreview.imageUrl, '_blank')}
              >
                Open in New Tab
              </Button>
              <Button 
                variant="outline"
                onClick={() => setApptImagePreview({ open: false, imageUrl: '', stopIndex: null })}
              >
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default LoadDetail;
